import React from 'react'
import { injectIntl, IntlShape } from 'react-intl'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { Table } from 'cimple-web-common'
import Topbar from 'components/Shared/Navigation/Topbar/Topbar'

import {
  LayoutColumn,
  LayoutTables
} from '../Shared/Layout/LayoutDetail/LayoutDetails'
import TableHeader from './Table/TableHeader'
import TableBody from './Table/TableBody'
import TablePagination from '../Shared/Pagination/Pagination'

import { Error, Loading } from '../Shared/States'

import { CATALOGUE_PAGE_SIZE } from 'constants/pageSize'
import { ERROR_RESULTS } from 'constants/errorTypes'
import { ROUTES } from 'constants/routes'
import { AFTER_LAST_SLASH } from 'utils/regex'
import { GET_CATALOGUE_SIGNPOST, DELETE_GOOD } from './query'

import { handleSearch } from 'utils/handleSearch'

import CatalogueSignpostGoodImport from '../CatalogueGoodImport/CatalogueSignpostGoodImport'

type CatalogueSignpostProps = {
  intl: IntlShape
}

const CatalogueSignpost: React.FC<CatalogueSignpostProps> = ({
  intl: { formatMessage: t }
}) => {
  const history = useHistory()
  const { pathname, search } = useLocation()

  const [page, setPage] = React.useState(1)
  const [searchTerm, setSearchTerm] = React.useState(search || null)
  const [isImportGoodsOpen, setIsImportGoodsOpen] = React.useState(false)
  const [catalogueDetailId, setCatalogueDetailId] = React.useState('')
  const [showOptions, setShowOptions] = React.useState('')

  const { data, loading, error, refetch } = useQuery(GET_CATALOGUE_SIGNPOST, {
    variables: {
      searchTerm: searchTerm,
      page: page,
      pageSize: CATALOGUE_PAGE_SIZE
    }
  })

  const handleSearchWithPayload = (values: any) =>
    handleSearch(history, values, setSearchTerm, ROUTES.catalogueSignpost)

  const handleModalClose = () => {
    history.push({
      pathname: history.location.pathname.replace('/import', '')
    })
  }

  const [deleteGood] = useMutation(DELETE_GOOD)

  const handleDeleteGood = () => {
    deleteGood({
      variables: {
        id: showOptions
      }
    }).then(({ data }) => {
      if (data) {
        refetch()
      }
    })
  }

  React.useEffect(() => {
    const checkForPathname = () => {
      const hasLength = pathname.match(AFTER_LAST_SLASH)

      if (hasLength && hasLength.length > 0) {
        return hasLength[0]
      }

      return null
    }

    if (checkForPathname() === 'import') {
      setIsImportGoodsOpen(
        (isImportGoodsOpen: boolean): boolean => !isImportGoodsOpen
      )
    }
  }, [pathname])

  const withActions = {
    primary: {
      actionName: 'Import goods',
      actionTo: `${ROUTES.catalogueSignpostGoodImport}`
    },
    secondary: {
      actionName: 'Add goods',
      actionTo: `${ROUTES.catalogueSignpostGoodCreate}`
    }
  }

  const withOptions = [
    {
      optionName: 'Delete',
      optionTo: () => handleDeleteGood()
    }
  ]

  const catalogue = data && data.results

  if (loading) {
    return <Loading />
  }

  if (error) {
    return <Error />
  }

  if (catalogue.entries.length === 0) {
    return (
      <LayoutColumn>
        <Topbar
          pageName={t({ id: 'catalogue.catalogue' })}
          totalEntries={catalogue.totalEntries}
          name="search"
          placeholder={t({ id: 'global.search' })}
          handleSearch={handleSearchWithPayload}
          withActions={withActions}
        />
        <Error errorType={ERROR_RESULTS} />
      </LayoutColumn>
    )
  }

  return (
    <LayoutColumn>
      <Topbar
        pageName={t({ id: 'catalogue.catalogue' })}
        name="search"
        totalEntries={catalogue.totalEntries}
        handleSearch={handleSearchWithPayload}
        placeholder={t({ id: 'global.search' })}
        withActions={withActions}
      />
      {isImportGoodsOpen && (
        <CatalogueSignpostGoodImport
          isOpen={isImportGoodsOpen}
          setIsImportGoodsOpen={setIsImportGoodsOpen}
          onClose={(isImportGoodsOpen: boolean) => {
            handleModalClose()
            setIsImportGoodsOpen(!isImportGoodsOpen)
          }}
        />
      )}
      <LayoutTables>
        <Table>
          <TableHeader />
          <TableBody
            catalogue={catalogue}
            catalogueDetailId={catalogueDetailId}
            setCatalogueDetailId={setCatalogueDetailId}
            showOptions={showOptions}
            setShowOptions={setShowOptions}
            withOptions={withOptions}
          />
        </Table>
        <TablePagination page={page} setPage={setPage} pageName={catalogue} />
      </LayoutTables>
    </LayoutColumn>
  )
}

export default injectIntl(CatalogueSignpost)
